<script setup lang="ts">
import type { SidebarProps } from "@/components/ui/sidebar"
import { useRouter, useRoute } from "vue-router"
import { computed } from "vue"
import { useAuthStore } from "@/stores/auth"
import { useRestaurantStore } from "@/stores/restaurant"
import { useI18n } from "@/composables/useI18n"

import {
  Home,
  ClipboardList,
  Menu,
  Settings,
  QrCode,
  Utensils,
  Activity,
  Building2,
  Users,
  Plus,
  Globe,
  ChevronRight,
  Languages,
  LifeBuoy,
} from "lucide-vue-next"
import NavUser from "@/components/NavUser.vue"
import TeamSwitcher from "@/components/TeamSwitcher.vue"
import packageJson from "../../package.json"

const { t } = useI18n()

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"

const props = withDefaults(defineProps<SidebarProps>(), {
  collapsible: "icon",
})

const router = useRouter()
const route = useRoute()

// Reaktywne dane
const authStore = useAuthStore()
const restaurantStore = useRestaurantStore()

const user = computed(() => authStore.user)
const restaurant = computed(() => restaurantStore.restaurant)

const isSuperAdmin = computed(() => user.value?.role === "superadmin")
const appVersion = packageJson.version

const hasFullPackage = computed(() => 
  restaurant.value?.packageType === "FULL" || !restaurant.value?.packageType
)

const isSettingsRoute = computed(() => 
  route.path.startsWith("/dashboard/settings")
)

const settingsMenuItems = computed(() => {
  const items = [
    { title: t("common.navigation.settingsBasic"), url: "/dashboard/settings/basic" },
    { title: t("common.navigation.settingsImages"), url: "/dashboard/settings/images" },
    { title: t("common.navigation.settingsHours"), url: "/dashboard/settings/hours" },
    { title: t("common.navigation.settingsSystem"), url: "/dashboard/settings/system" },
  ]
  
  if (hasFullPackage.value) {
    items.splice(3, 0, { title: t("common.navigation.settingsPayment"), url: "/dashboard/settings/payment" })
    items.splice(4, 0, { title: t("common.navigation.settingsNotifications"), url: "/dashboard/settings/notifications" })
  }
  
  return items
})

const teams = computed(() => [
  {
    name: restaurant.value?.name || "-",
    logo: Home,
  },
])

const navMain = computed(() => {
  const restaurantMenu = {
    title: t("common.navigation.restaurant"),
    items: [
      {
        title: t("common.navigation.activeOrders"),
        url: "/dashboard/active-orders",
        icon: Activity,
        isActive: route.path === "/dashboard/active-orders",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.orderHistory"),
        url: "/dashboard/orders",
        icon: ClipboardList,
        isActive: route.path === "/dashboard/orders",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.menuManagement"),
        url: "/dashboard/menu",
        icon: Menu,
        isActive: route.path === "/dashboard/menu",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.menuTranslations"),
        url: "/dashboard/menu/translations",
        icon: Languages,
        isActive: route.path === "/dashboard/menu/translations",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.products"),
        url: "/dashboard/products",
        icon: Utensils,
        isActive: route.path === "/dashboard/products",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.tablesAndQr"),
        url: "/dashboard/tables",
        icon: QrCode,
        isActive: route.path === "/dashboard/tables",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.manageRestaurantGroups"),
        url: "/dashboard/restaurant-groups",
        icon: Globe,
        isActive: route.path === "/dashboard/restaurant-groups",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.settings"),
        icon: Settings,
        isActive: isSettingsRoute.value,
        isCollapsible: true,
        items: settingsMenuItems.value,
      },
    ],
  }

  const superAdminMenu = {
    title: t("common.navigation.administration"),
    items: [
      {
        title: t("common.navigation.allOrders"),
        url: "/dashboard/admin/orders",
        icon: ClipboardList,
        isActive: route.path === "/dashboard/admin/orders",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.newRestaurant"),
        url: "/dashboard/admin/restaurants/new",
        icon: Plus,
        isActive: route.path === "/dashboard/admin/restaurants/new",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.manageRestaurants"),
        url: "/dashboard/admin/restaurants",
        icon: Building2,
        isActive: route.path === "/dashboard/admin/restaurants",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.manageUsers"),
        url: "/dashboard/admin/users",
        icon: Users,
        isActive: route.path === "/dashboard/admin/users",
        isCollapsible: false,
        items: [],
      },
      {
        title: t("common.navigation.manageRestaurantGroups"),
        url: "/dashboard/admin/restaurant-groups",
        icon: Globe,
        isActive: route.path === "/dashboard/admin/restaurant-groups",
        isCollapsible: false,
        items: [],
      },
    ],
  }

  // Filtruj items dla pakietu SIMPLE
  const restrictedUrls = [
    "/dashboard/active-orders",
    "/dashboard/orders",
  ]
  
  const filteredRestaurantMenu = {
    ...restaurantMenu,
    items: restaurantMenu.items.filter(
      (item) => hasFullPackage.value || !restrictedUrls.includes(item.url || "")
    ),
  }

  // Ukryj menu "Restauracja" jeśli nie ma aktywnej restauracji
  const menus = []
  if (restaurant.value) {
    menus.push(filteredRestaurantMenu)
  }
  if (isSuperAdmin.value) {
    menus.push(superAdminMenu)
  }
  return menus
})
</script>

<template>
  <Sidebar v-bind="props">
    <SidebarHeader>
      <TeamSwitcher :teams="teams" />
    </SidebarHeader>
    <SidebarContent>
      <SidebarGroup v-for="item in navMain" :key="item.title">
        <SidebarGroupLabel>{{ item.title }}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <template v-for="childItem in item.items" :key="childItem.title">
              <!-- Collapsible item (Settings) -->
              <Collapsible
                v-if="childItem.isCollapsible"
                as-child
                :default-open="childItem.isActive"
                class="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger as-child>
                    <SidebarMenuButton :is-active="childItem.isActive">
                      <component :is="childItem.icon" class="mr-3 h-5 w-5" />
                      <span>{{ childItem.title }}</span>
                      <ChevronRight class="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub class="pl-6">
                      <SidebarMenuSubItem
                        v-for="subItem in childItem.items"
                        :key="subItem.title"
                      >
                        <SidebarMenuSubButton
                          as-child
                          :is-active="route.path === subItem.url"
                          @click="router.push(subItem.url)"
                        >
                          <button class="w-full text-left">
                            <span>{{ subItem.title }}</span>
                          </button>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              <!-- Regular item -->
              <SidebarMenuItem v-else>
                <SidebarMenuButton
                  as-child
                  :is-active="childItem.isActive"
                  @click="router.push(childItem.url as string)"
                >
                  <button class="flex items-center w-full">
                    <component :is="childItem.icon" class="mr-3 h-5 w-5" />
                    <span>{{ childItem.title }}</span>
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </template>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>
    <SidebarFooter>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            as-child
            :is-active="route.path === '/dashboard/help'"
            @click="router.push('/dashboard/help')"
          >
            <button class="flex items-center w-full">
              <LifeBuoy class="mr-3 h-5 w-5" />
              <span>{{ t("common.navigation.help") }}</span>
            </button>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      <NavUser
        v-if="user"
        :user="{
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email,
        }"
        :version="appVersion"
      />
    </SidebarFooter>
    <SidebarRail />
  </Sidebar>
</template>
